import { z } from "zod";
import { createEndpoint, StandardSuccessResponseSchema } from "../createEndpoint";
import type { HttpClient, QueryParams } from "../httpClient";
import {
    CreatePlanInvitationRequestSchema,
    CreatePlanBillingOptionRequestSchema,
    PlanBillingOptionSchema,
    PlanInvitationSchema,
    UpdatePlanBillingOptionRequestSchema,
} from "../schemas/expert-billing";

/**
 * Admin Phase 0 billing surface. Billing options are children of expert
 * plans; Stripe mode is derived server-side from the configured key and the
 * sync endpoint returns the refreshed option even on provider failure (502).
 */
export function createAdminExpertBillingApi(http: HttpClient, baseUrl: string) {
    const adminPlansBaseUrl = `${baseUrl}/admin/expert-plans`;
    const adminExpertsBaseUrl = `${baseUrl}/admin/experts`;

    return {
        listBillingOptions: createEndpoint<
            z.infer<typeof PlanBillingOptionSchema>[],
            unknown,
            QueryParams,
            { plan: number }
        >(http, "GET", "/:plan/billing-options", {
            base: adminPlansBaseUrl,
            responseSchema: z.array(PlanBillingOptionSchema),
            params: ["plan"],
        }),

        getBillingOption: createEndpoint<
            z.infer<typeof PlanBillingOptionSchema>,
            unknown,
            QueryParams,
            { plan: number; billingOption: number }
        >(http, "GET", "/:plan/billing-options/:billingOption", {
            base: adminPlansBaseUrl,
            responseSchema: PlanBillingOptionSchema,
            params: ["plan", "billingOption"],
        }),

        createBillingOption: createEndpoint<
            z.infer<typeof PlanBillingOptionSchema>,
            z.input<typeof CreatePlanBillingOptionRequestSchema>,
            QueryParams,
            { plan: number }
        >(http, "POST", "/:plan/billing-options", {
            base: adminPlansBaseUrl,
            responseSchema: PlanBillingOptionSchema,
            params: ["plan"],
        }),

        updateBillingOption: createEndpoint<
            z.infer<typeof PlanBillingOptionSchema>,
            z.input<typeof UpdatePlanBillingOptionRequestSchema>,
            QueryParams,
            { plan: number; billingOption: number }
        >(http, "PUT", "/:plan/billing-options/:billingOption", {
            base: adminPlansBaseUrl,
            responseSchema: PlanBillingOptionSchema,
            params: ["plan", "billingOption"],
        }),

        deleteBillingOption: createEndpoint<
            z.infer<typeof StandardSuccessResponseSchema>,
            unknown,
            QueryParams,
            { plan: number; billingOption: number }
        >(http, "DELETE", "/:plan/billing-options/:billingOption", {
            base: adminPlansBaseUrl,
            responseSchema: StandardSuccessResponseSchema,
            params: ["plan", "billingOption"],
        }),

        syncBillingOptionStripe: createEndpoint<
            z.infer<typeof PlanBillingOptionSchema>,
            unknown,
            QueryParams,
            { plan: number; billingOption: number }
        >(http, "POST", "/:plan/billing-options/:billingOption/stripe-sync", {
            base: adminPlansBaseUrl,
            responseSchema: PlanBillingOptionSchema,
            params: ["plan", "billingOption"],
        }),

        listPlanInvitations: createEndpoint<
            z.infer<typeof PlanInvitationSchema>[],
            unknown,
            QueryParams,
            { expert: number }
        >(http, "GET", "/:expert/plan-invitations", {
            base: adminExpertsBaseUrl,
            responseSchema: z.array(PlanInvitationSchema),
            params: ["expert"],
        }),

        createPlanInvitation: createEndpoint<
            z.infer<typeof PlanInvitationSchema>,
            z.input<typeof CreatePlanInvitationRequestSchema>,
            QueryParams,
            { expert: number }
        >(http, "POST", "/:expert/plan-invitations", {
            base: adminExpertsBaseUrl,
            responseSchema: PlanInvitationSchema,
            params: ["expert"],
        }),

        cancelPlanInvitation: createEndpoint<
            z.infer<typeof PlanInvitationSchema>,
            unknown,
            QueryParams,
            { expert: number; planInvitation: number }
        >(http, "POST", "/:expert/plan-invitations/:planInvitation/cancel", {
            base: adminExpertsBaseUrl,
            responseSchema: PlanInvitationSchema,
            params: ["expert", "planInvitation"],
        }),
    };
}

export type AdminExpertBillingApi = ReturnType<typeof createAdminExpertBillingApi>;
